import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
    useFiltersApi,
    BenchmarkingDisplay,
    BenchmarkingReportSettings,
    BenchmarkingRequestParams,
} from '@/hooks'
import useAnalyticsStore from '@/features/Analytics/stores/useAnalyticsStore/useAnalyticsStore'
import { useAnalyticsFiltersGetParams } from '@/features/Analytics/hooks/useAnalyticsFiltersGetParams'
import { Option } from '@/components/common/Form/types'
import { flattenNodes } from '@/utils/flattenNodes'
import { mapBenchmarkingParamsToFilters } from '@/features/Reporting/utils/mapBenchmarkingParamsToFilters'
import { useRemoveRootNodeIdFilter } from '@/features/Analytics/hooks/useRemoveRootNodeIdFilter'

const MAX_CHART_DISPLAY_COUNT = 20

export const useBenchmarkingDisplayManager = (
    reportSettings: BenchmarkingReportSettings,
    filters: BenchmarkingRequestParams
) => {
    const { t } = useTranslation('translations')

    const currentDisplay = useAnalyticsStore(
        (state) => state.benchmarking.reportSettings.display
    )

    const selectedView = useAnalyticsStore(
        (state) => state.benchmarking.selectedView
    )

    const normalizedNodeIdsFilters = useRemoveRootNodeIdFilter(filters)
    const parsedFilters = mapBenchmarkingParamsToFilters(
        normalizedNodeIdsFilters
    )

    const params = useAnalyticsFiltersGetParams(parsedFilters)

    const {
        useNodesQuery,
        useFacilitiesQuery,
        useTagsQuery,
        useUSNsQuery,
        useMetersQuery,
    } = useFiltersApi()

    const { data: nodes } = useNodesQuery()

    const { data: tags } = useTagsQuery({
        'nodeIds[]': params['nodeIds[]'],
        'commodityTypeIds[]': params.commodityTypeId,
        'providerIds[]': params['providerIds[]'],
        'contractIds[]': params['contractIds[]'],
        'panIds[]': params['panIds[]'],
    })

    const { data: facilities } = useFacilitiesQuery({
        'nodeIds[]': params['nodeIds[]'],
        'commodityTypeIds[]': params.commodityTypeId,
        'providerIds[]': params['providerIds[]'],
        'contractIds[]': params['contractIds[]'],
        'panIds[]': params['panIds[]'],
        'tagIds[]': params['tagIds[]'],
    })

    const { data: usns } = useUSNsQuery({
        'nodeIds[]': params['nodeIds[]'],
        'commodityTypeIds[]': params.commodityTypeId,
        'providerIds[]': params['providerIds[]'],
        'contractIds[]': params['contractIds[]'],
        'panIds[]': params['panIds[]'],
        'tagIds[]': params['tagIds[]'],
        'facilityIds[]': params['facilityIds[]'],
    })

    const { data: meters } = useMetersQuery({
        'nodeIds[]': params['nodeIds[]'],
        'commodityTypeIds[]': params.commodityTypeId,
        'providerIds[]': params['providerIds[]'],
        'contractIds[]': params['contractIds[]'],
        'panIds[]': params['panIds[]'],
        'tagIds[]': params['tagIds[]'],
        'facilityIds[]': params['facilityIds[]'],
        'usnAccountIds[]': params['usnAccountIds[]'],
    })

    const getCountForDisplay = useCallback(
        (display: BenchmarkingDisplay): number => {
            const nodesCount = nodes
                ? flattenNodes({
                      nodes: nodes.nodes,
                      getChildren: (node) => node.nodes,
                  }).length
                : 0

            const counts = {
                node: parsedFilters.node.length || nodesCount || 0,
                group: parsedFilters.tag.length || tags?.length || 0,
                facility:
                    parsedFilters.facility.length || facilities?.length || 0,
                usn: parsedFilters.usn?.length || usns?.length || 0,
                meter: parsedFilters.meter.length || meters?.length || 0,
            }

            if (display in counts) {
                return counts[display]
            }

            return 0
        },
        [
            nodes,
            parsedFilters.node.length,
            parsedFilters.tag.length,
            parsedFilters.facility.length,
            parsedFilters.usn?.length,
            parsedFilters.meter.length,
            tags?.length,
            facilities?.length,
            usns?.length,
            meters?.length,
        ]
    )

    const getDisplayCount = useCallback(
        (display: BenchmarkingDisplay): number => {
            const count = getCountForDisplay(display)

            if (selectedView === 'chart' && count > MAX_CHART_DISPLAY_COUNT) {
                return MAX_CHART_DISPLAY_COUNT
            }

            return count
        },
        [getCountForDisplay, selectedView]
    )

    const showAlert = useMemo(() => {
        const currentCount = getCountForDisplay(currentDisplay)
        return (
            selectedView === 'chart' && currentCount > MAX_CHART_DISPLAY_COUNT
        )
    }, [getCountForDisplay, currentDisplay, selectedView])

    const options = useMemo(() => {
        const options: Array<Option<BenchmarkingDisplay> & { count?: number }> =
            [
                {
                    value: 'node',
                    label: t('filters.node.label_plural'),
                    count: getDisplayCount('node'),
                },
                {
                    value: 'group',
                    label: t('filters.tag.label_plural'),
                    count: getDisplayCount('group'),
                },
                {
                    value: 'facility',
                    label: t('filters.facility.label_plural'),
                    count: getDisplayCount('facility'),
                },
                {
                    value: 'usn',
                    label: t('filters.usn.label_plural'),
                    count: getDisplayCount('usn'),
                },
                {
                    value: 'meter',
                    label: t('filters.meter.label_plural'),
                    count: getDisplayCount('meter'),
                },
            ]

        return options.filter(
            (option) =>
                reportSettings.basis !== 'emission' ||
                !['node', 'usn', 'meter'].includes(option.value)
        )
    }, [t, getDisplayCount, reportSettings.basis])

    return {
        showAlert,
        options,
    }
}
